import { useCallback, useMemo } from 'react'

import { Plus } from 'lucide-react'
import type { FC } from 'react'

import { Button } from '@/components/ui/button'

import type {
	SelectFilterDefinition,
	TextFilterDefinition,
} from '../TableFilters'
import { FilterBox } from './FilterBox'
import { FilterRow } from './FilterRow'
import type { CustomFilterShape } from './useFilters'
import {
	addOperatorsToOptions,
	convertFilterArrayToObject,
	filterIncompleteFilters,
} from './util'

interface FilterListProps {
	open: boolean
	closeAndRemoveEmpty: () => void
	filters: Array<CustomFilterShape>
	options: Array<SelectFilterDefinition | TextFilterDefinition>
	onFilterChange: (newValue: CustomFilterShape, index: number) => void
}

export const FilterList: FC<FilterListProps> = ({
	closeAndRemoveEmpty,
	filters,
	options,
	onFilterChange,
}) => {
	const optionsWithOperators = useMemo(
		() => options.map(addOperatorsToOptions),
		[options]
	)

	const filtersAsObject = useMemo(
		() => convertFilterArrayToObject(filters),
		[filters]
	)

	const hasIncompleteFilters = useMemo(
		() => filterIncompleteFilters(filters).length !== filters.length,
		[filters]
	)

	const availableFiltersToAdd = useMemo(
		() =>
			optionsWithOperators.filter(
				option => !(option.column.value in filtersAsObject)
			),
		[filtersAsObject, optionsWithOperators]
	)

	const addNewFilterRow = useCallback(() => {
		const { column, operators } = availableFiltersToAdd[0]
		const newFilter = {
			[column.value]: {
				[operators[0].value]: null,
			},
		}
		onFilterChange(newFilter, filters.length ? filters.length + 1 : 1)
	}, [availableFiltersToAdd, filters, onFilterChange])

	return (
		<>
			<FilterBox
				filters={filters}
				closeAndRemoveEmpty={closeAndRemoveEmpty}
			/>
			{filters.map((filter, i) => {
				const column = Object.keys(filter)[0]

				/**
				 * Include the options that have not been selected and
				 * The selected option when it's the current entry
				 */
				const filteredOptions = optionsWithOperators.filter(op => {
					return (
						!(op.column.value in filtersAsObject) ||
						op.column.value === column
					)
				})

				return (
					<FilterRow
						key={column}
						filter={filter}
						index={i}
						options={filteredOptions}
						onChange={onFilterChange}
					/>
				)
			})}

			{/* Add new rows */}
			{!hasIncompleteFilters && availableFiltersToAdd.length > 0 && (
				<Button
					onClick={addNewFilterRow}
					variant="outline"
					className="w-full"
				>
					<Plus /> Add Condition
				</Button>
			)}
		</>
	)
}
